/**
 * The passenger questionnaire of the CONFIRM step (TECH-1097), shared by the API (validation,
 * `passenger_note` rendering, admin follow-ups), the signature page (the questions themselves) and
 * the admin dashboards (question blocks, answers display).
 *
 * Every value below is stored as-is in `family_claims_acquisitions.passenger_answers`: renaming
 * one breaks the answers already given. Add, never rename.
 */

export const PASSENGER_ANSWERS_VERSION = 1;

export const QUESTIONNAIRE_LOCALES = ["fr", "en"] as const;
export type QuestionnaireLocale = (typeof QUESTIONNAIRE_LOCALES)[number];

/**
 * The question blocks an admin can switch on or off per claim (`claims.passenger_question_blocks`).
 * The other passengers' addresses are not one of them: the confirmation modal asks for them first.
 */
export const QUESTION_BLOCKS = ["REROUTING", "AIRLINE_REASON", "EXTRAORDINARY_CIRCUMSTANCES"] as const;
export type QuestionBlock = (typeof QUESTION_BLOCKS)[number];

export const VERIFICATION_STATUSES = ["CONFIRMED", "CORRECTED", "NOT_TRAVELLED"] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

/** What the passenger says we got wrong. `NO_INCIDENT` sends the file to review, like not travelling. */
export const CORRECTION_ITEMS = ["FLIGHT", "TIMES", "INCIDENT_TYPE", "NO_INCIDENT"] as const;
export type CorrectionItem = (typeof CORRECTION_ITEMS)[number];

export const NOT_TRAVELLED_REASONS = ["OTHER_FLIGHT", "PLANS_CHANGED", "HEALTH_OR_FAMILY", "OTHER"] as const;
export type NotTravelledReason = (typeof NOT_TRAVELLED_REASONS)[number];

/** Asked as « la compagnie vous a-t-elle proposé un autre vol ? »: `AIRLINE` or `NOT_REROUTED`. */
export const REROUTING_MODES = [
  "AIRLINE",
  "SELF_BOOKED",
  "OTHER_TRANSPORT",
  "ABANDONED",
  "NOT_REROUTED",
] as const;
export type ReroutingMode = (typeof REROUTING_MODES)[number];

/**
 * Arrival at the final destination, compared with the scheduled arrival. Ranges rather than a time
 * picker: no date, time zone or airport to ask. The thresholds and amounts are never shown next to
 * them, so nobody is nudged into overstating.
 */
export const ARRIVAL_DELAYS = [
  "AROUND_SCHEDULED",
  "BETWEEN_2_AND_3_HOURS",
  "BETWEEN_3_AND_4_HOURS",
  "MORE_THAN_4_HOURS",
  "NEXT_DAY_OR_LATER",
  "BETWEEN_1_AND_2_HOURS",
  "THREE_HOURS_OR_MORE",
] as const;
export type ArrivalDelay = (typeof ARRIVAL_DELAYS)[number];

/** The ranges offered now; the others stay readable in answers already given. */
export const ASKED_ARRIVAL_DELAYS: readonly ArrivalDelay[] = [
  "AROUND_SCHEDULED",
  "BETWEEN_1_AND_2_HOURS",
  "THREE_HOURS_OR_MORE",
  "NEXT_DAY_OR_LATER",
];

export const INFORMATION_TIMINGS = [
  "MORE_THAN_14_DAYS",
  "BETWEEN_7_AND_14_DAYS",
  "LESS_THAN_7_DAYS",
  "AT_AIRPORT",
  "NEVER",
] as const;
export type InformationTiming = (typeof INFORMATION_TIMINGS)[number];

export const INFORMATION_CHANNELS = ["EMAIL", "SMS", "APP", "TRAVEL_AGENCY", "AIRPORT"] as const;
export type InformationChannel = (typeof INFORMATION_CHANNELS)[number];

/** `NONE` is exclusive: picking it clears the others, picking another clears it. */
export const PRIOR_STEPS = [
  "NONE",
  "CLAIM_PENDING",
  "COMPENSATED",
  "VOUCHER_ACCEPTED",
  "TICKET_REFUNDED",
] as const;
export type PriorStep = (typeof PRIOR_STEPS)[number];

export const AIRLINE_REASONS = ["TECHNICAL", "WEATHER", "STRIKE", "NONE_GIVEN", "OTHER"] as const;
export type AirlineReason = (typeof AIRLINE_REASONS)[number];

export const CONSEQUENCES = [
  "MISSED_CONNECTION",
  "UNPLANNED_NIGHT",
  "WORK_OR_APPOINTMENT",
  "MISSED_EVENT",
  "LOST_HOLIDAY",
  "VULNERABLE_PERSON",
  "STRESS_FATIGUE",
] as const;
export type Consequence = (typeof CONSEQUENCES)[number];

/** Out of Regulation 261/2004: each one becomes an admin follow-up, handled separately. */
export const OTHER_ISSUES = ["BAGGAGE", "DOWNGRADE", "PAID_SERVICE_NOT_PROVIDED"] as const;
export type OtherIssue = (typeof OTHER_ISSUES)[number];

/**
 * - `FAMILY`: a passenger of the signer's own file (signed by this acquisition).
 * - `OTHER_FAMILY`: a passenger of the booking we have never been able to write to.
 */
export const CO_PASSENGER_SCOPES = ["FAMILY", "OTHER_FAMILY"] as const;
export type CoPassengerScope = (typeof CO_PASSENGER_SCOPES)[number];

export const CO_PASSENGER_STATUSES = [
  "TRAVELLED",
  "EMAIL",
  "SAME_AS_ME",
  "MINOR_REPRESENTED",
  "DID_NOT_TRAVEL",
  "UNKNOWN",
] as const;
export type CoPassengerStatus = (typeof CO_PASSENGER_STATUSES)[number];

/** Which statuses each kind of passenger can take. */
export const CO_PASSENGER_STATUSES_BY_SCOPE: Record<CoPassengerScope, readonly CoPassengerStatus[]> = {
  FAMILY: ["TRAVELLED", "MINOR_REPRESENTED", "DID_NOT_TRAVEL"],
  OTHER_FAMILY: ["EMAIL", "SAME_AS_ME", "MINOR_REPRESENTED", "DID_NOT_TRAVEL", "UNKNOWN"],
};

export type CoPassengerAnswer = {
  userClaimId: string;
  familyId: string;
  scope: CoPassengerScope;
  firstName: string;
  lastName: string;
  status: CoPassengerStatus;
  /** Only with `EMAIL`. */
  email?: string | null;
};

export type PassengerAnswers = {
  version: typeof PASSENGER_ANSWERS_VERSION;
  /** The language the passenger answered in. The `passenger_note` is always rendered in French. */
  locale: QuestionnaireLocale;
  verification: {
    status: VerificationStatus;
    /** With `CORRECTED`. */
    corrections?: CorrectionItem[];
    /** With `NOT_TRAVELLED`. */
    notTravelledReason?: NotTravelledReason | null;
    /** Free text for either: what was different, or why they did not fly. */
    details?: string | null;
  };
  rerouting?: {
    mode: ReroutingMode;
    arrivalDelay?: ArrivalDelay | null;
    /** Optional: the flight the airline offered instead, as typed (« AF1234 »). */
    flightNumber?: string | null;
  };
  passengerInformation?: {
    timing: InformationTiming;
    channel?: InformationChannel | null;
  };
  priorSteps?: {
    steps: PriorStep[];
    claimReference?: string | null;
    compensationAmount?: number | null;
  };
  coPassengers?: CoPassengerAnswer[];
  expenses?: { hasExpenses: boolean };
  /** « Avez-vous déjà réclamé une indemnisation ? », stored as well in `preExistingClaim`. */
  preExistingClaim?: boolean;
  airlineReason?: AirlineReason | null;
  /** With `OTHER`: the reason in the passenger's words. */
  airlineReasonDetails?: string | null;
  consequences?: Consequence[];
  otherIssues?: OtherIssue[];
  precisions?: {
    /** The admin's custom question, as it read when the passenger answered it. */
    question?: string | null;
    text?: string | null;
  };
};

/** What the questionnaire needs to know about the claim to decide which screens to show. */
export type QuestionnaireContext = {
  claimTypeName: string | null | undefined;
  blocks: readonly QuestionBlock[];
};
